/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Fab from "@mui/material/Fab";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";

// Icons
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

// Modal
import ModalDefault from "components/Modals";

// Pages
import Dashboard from "components/HelpFloatingButton/pages/Dashboard";
import CommunityInfo from "components/HelpFloatingButton/pages/CommunityInfo";
import CommunityStructure from "components/HelpFloatingButton/pages/CommunityStructure";
import MemberList from "components/HelpFloatingButton/pages/MemberList";
import MemberEdit from "components/HelpFloatingButton/pages/MemberEdit";
import ResourceCreation from "components/HelpFloatingButton/pages/ResourceCreation";
import ResourceList from "components/HelpFloatingButton/pages/ResourceList";
import ResourceEdit from "components/HelpFloatingButton/pages/ResourceEdit";
import Meters from "components/HelpFloatingButton/pages/Meters";
import Contracts from "components/HelpFloatingButton/pages/Contracts";
import OpportunityCostsList from "components/HelpFloatingButton/pages/OpportunityCostsList";
import RESP from "components/HelpFloatingButton/pages/RESP";
import ERSE from "components/HelpFloatingButton/pages/ERSE";
import Loads from "components/HelpFloatingButton/pages/Loads";
import DSO from "components/HelpFloatingButton/pages/DSO";
import DSOList from "components/HelpFloatingButton/pages/DSOList";
import MarketList from "components/HelpFloatingButton/pages/MarketList";
import MarketAnalytics from "components/HelpFloatingButton/pages/MarketAnalytics";
import SettlementList from "components/HelpFloatingButton/pages/SettlementList";
import SettlementAnalytics from "components/HelpFloatingButton/pages/SettlementAnalytics";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : theme.palette.dark.main,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0px 4px 10px rgba(255, 255, 255, 0.3)"
        : "0px 4px 10px rgba(0, 0, 0, 0.8)",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5" : theme.palette.dark.focus,
    },
    zIndex: 1000,
  },
  icon: {
    width: "32px",
    height: "32px",
    color: theme.palette.mode === "dark" ? theme.palette.dark.main : theme.palette.white.main,
  },
}));

function HelpFloatingButton({ helpScreen }) {
  const { t } = useTranslation(["help"]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const classes = useStyles();
  let helpScreenComponent;

  switch (helpScreen) {
    case "dashboard":
      helpScreenComponent = <Dashboard />;
      break;
    case "community-info":
      helpScreenComponent = <CommunityInfo />;
      break;
    case "community-structure":
      helpScreenComponent = <CommunityStructure />;
      break;
    case "member-list":
      helpScreenComponent = <MemberList />;
      break;
    case "member-edit":
      helpScreenComponent = <MemberEdit />;
      break;
    case "resource-creation":
      helpScreenComponent = <ResourceCreation />;
      break;
    case "resource-list":
      helpScreenComponent = <ResourceList />;
      break;
    case "resource-edit":
      helpScreenComponent = <ResourceEdit />;
      break;
    case "meters":
      helpScreenComponent = <Meters />;
      break;
    case "contracts":
      helpScreenComponent = <Contracts />;
      break;
    case "opportunity-costs-list":
      helpScreenComponent = <OpportunityCostsList />;
      break;
    case "resp":
      helpScreenComponent = <RESP />;
      break;
    case "erse":
      helpScreenComponent = <ERSE />;
      break;
    case "loads":
      helpScreenComponent = <Loads />;
      break;
    case "dso-analytics":
      helpScreenComponent = <DSO />;
      break;
    case "dso-list":
      helpScreenComponent = <DSOList />;
      break;
    case "market-list":
      helpScreenComponent = <MarketList />;
      break;
    case "market-analytics":
      helpScreenComponent = <MarketAnalytics />;
      break;
    case "settlement-list":
      helpScreenComponent = <SettlementList />;
      break;
    case "settlement-analytics":
      helpScreenComponent = <SettlementAnalytics />;
      break;
    default:
      helpScreenComponent = <p>{t("help_titles.help-text-missing")}</p>;
  }

  return (
    <Box>
      <Fab className={classes.fab} onClick={handleOpen}>
        <QuestionMarkIcon className={classes.icon} size="large" />
      </Fab>

      <ModalDefault open={open} onClickClose={handleClose} width="80%" top="50%" pl={0} pr={0}>
        {helpScreenComponent}
      </ModalDefault>
    </Box>
  );
}

HelpFloatingButton.propTypes = {
  helpScreen: PropTypes.string.isRequired,
};

export default HelpFloatingButton;
